/**
 * ROI 편집기 상단 맵 패널 — 현장 맵을 고르면 도면이 그려지고, 맵에 배치된 CCTV 가
 * 좌측 목록이 된다. 마커를 클릭하면 그 CCTV 로 전환한다.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Box, Collapse, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SiteMapCanvas from '../../../components/SiteMap/SiteMapCanvas';
import { useSiteMap } from '../../../components/SiteMap/useSiteMap';
import { SimulatorService } from '../../../services/SimulatorService';
import { SimulatorMap } from '../../../models/Simulator';

interface RoiWorkMapPanelProps {
  projectId: string;
  open: boolean;
  onToggle: () => void;
  selectedCctv: string;
  onCctvSelect: (cctvId: string) => void;
  onMapCctvs: (cctvs: { name: string; ip: string }[], mapName: string) => void;
}

export const RoiWorkMapPanel: React.FC<RoiWorkMapPanelProps> = ({
  projectId,
  open,
  onToggle,
  selectedCctv,
  onCctvSelect,
  onMapCctvs,
}) => {
  const [maps, setMaps] = useState<SimulatorMap[]>([]);
  const [mapId, setMapId] = useState<number | null>(null);

  useEffect(() => {
    const siteProjectId = Number(projectId);
    if (!Number.isFinite(siteProjectId)) return;
    let cancelled = false;
    SimulatorService.listMaps(siteProjectId)
      .then((list) => !cancelled && setMaps(list))
      .catch(() => !cancelled && setMaps([]));
    return () => { cancelled = true; };
  }, [projectId]);

  const mapName = maps.find((m) => m.id === mapId)?.name || '';
  const siteMap = useSiteMap(mapId, mapName);

  useEffect(() => {
    if (siteMap.cameras.length > 0) {
      onMapCctvs(siteMap.cameras.map(({ name, ip }) => ({ name, ip })), mapName || '맵');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteMap.cameras]);

  return (
    <Box sx={{ px: 2, pt: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <IconButton size="small" onClick={onToggle} aria-label="맵 패널 접기">
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle2">현장 맵</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>맵</InputLabel>
          <Select
            label="맵"
            value={mapId === null ? '' : String(mapId)}
            onChange={(e) => setMapId(e.target.value ? Number(e.target.value) : null)}
          >
            <MenuItem value=""><em>선택 안 함</em></MenuItem>
            {maps.map((m) => <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>)}
          </Select>
        </FormControl>
        {mapId !== null && (
          <Typography variant="caption" color="text.secondary">
            맵 CCTV {siteMap.cameras.length}대 · 마커 클릭으로 전환
          </Typography>
        )}
      </Stack>
      <Collapse in={open && mapId !== null} unmountOnExit>
        {siteMap.error && <Alert severity="warning" sx={{ mb: 1 }}>{siteMap.error}</Alert>}
        <Box sx={{ pb: 1 }}>
          <SiteMapCanvas
            levels={siteMap.levels}
            selectedCctv={selectedCctv}
            onCctvClick={(cam) => onCctvSelect(cam.name)}
            height={300}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
